import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from '@tanstack/react-router';
import { pedir } from '../api/cliente';
import { Boton, Encabezado, Insignia, Tarjeta } from '../componentes/ui/basicos';
import { Campo } from '../componentes/ui/formulario';
import { Cargando, Problema, Vacio, useTextoDeError } from '../componentes/ui/estados';
import { Tabla } from '../componentes/ui/tabla';
import { usePermiso } from '../sesion/ProveedorDeSesion';
import { DatosDelTercero } from '../componentes/DatosDelTercero';
import { importeParaMirar } from '../lib/numero';
import type { Tercero } from './Proveedores';

/**
 * El número tal como está en el papel del proveedor, con lo que haya.
 *
 * **La letra y el punto de venta pueden faltar**: una factura cargada sin ellos es válida, y
 * pegarlos igual dibujaba «null null-0001-00000001», que se ve exactamente como un defecto porque
 * lo es. Se arma con lo que hay.
 */
function numeroDeLaFactura(factura: FacturaDeProveedor): string {
  const partes = [factura.letra, factura.puntoDeVentaDelProveedor, factura.numeroDelProveedor];
  return partes.filter((parte) => parte !== null && parte !== '').join(' ');
}

/** `parcial` → `compras.estadoParcial`, que es como están escritas las claves de estado. */
function claveDeEstado(estado: string): string {
  return `compras.estado${estado.charAt(0).toUpperCase()}${estado.slice(1)}`;
}

interface MovimientoDeCuenta {
  readonly id: string;
  readonly importe: string;
  readonly concepto: string | null;
  readonly tipo: string;
  readonly ocurridoEn: string;
}

interface CuentaDelTercero {
  readonly saldo: string;
  readonly movimientos: readonly MovimientoDeCuenta[];
}

interface Orden {
  readonly id: string;
  readonly numero: string;
  readonly fecha: string;
  readonly estado: string;
  readonly total: string;
}

interface Recepcion {
  readonly id: string;
  readonly numero: string;
  readonly fecha: string;
  readonly estado: string;
  readonly remitoDelProveedor: string | null;
}

interface FacturaDeProveedor {
  readonly id: string;
  readonly letra: string | null;
  readonly puntoDeVentaDelProveedor: string | null;
  readonly numeroDelProveedor: string;
  readonly fecha: string;
  readonly total: string;
  readonly estado: string;
  readonly tieneDiferencias: boolean;
}

/**
 * La ficha de un proveedor: quién es y qué le compramos.
 *
 * El circuito de compra entero en una pantalla —órdenes, recepciones, facturas— más lo que se le
 * debe. Los datos del tercero son **el mismo componente** que en la ficha del cliente: lo que se
 * duplica es la ruta, no el formulario.
 *
 * El saldo es el del **plano proveedor** y no se compensa con el de cliente, aunque el tercero sea
 * las dos cosas: es la regla de E5.
 */
export function FichaDeProveedor(): React.ReactElement {
  const { t, i18n } = useTranslation();
  const { id } = useParams({ from: '/proveedores/$id' });
  const cola = useQueryClient();
  const textoDeError = useTextoDeError();

  const puedeEditar = usePermiso('TERCEROS.TERCERO.EDITAR');
  const puedeEditarPerfil = usePermiso('TERCEROS.PROVEEDOR.EDITAR');
  const puedeVerCuentas = usePermiso('CUENTAS.CUENTA.VER');
  const puedeVerOrdenes = usePermiso('COMPRAS.ORDEN.VER');
  const puedeVerRecepciones = usePermiso('COMPRAS.RECEPCION.VER');
  const puedeVerFacturas = usePermiso('COMPRAS.FACTURA.VER');

  const [dias, setDias] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const tercero = useQuery({
    queryKey: ['tercero', id],
    queryFn: () => pedir<Tercero>(`/terceros/${id}`),
  });
  const cuenta = useQuery({
    queryKey: ['cuenta-del-tercero', id, 'proveedor'],
    enabled: puedeVerCuentas,
    queryFn: () => pedir<CuentaDelTercero>(`/cuentas-corrientes/${id}?plano=proveedor`),
  });
  const ordenes = useQuery({
    queryKey: ['ordenes-del-proveedor', id],
    enabled: puedeVerOrdenes,
    queryFn: () => pedir<Orden[]>(`/ordenes-de-compra?proveedorId=${id}`),
  });
  const recepciones = useQuery({
    queryKey: ['recepciones-del-proveedor', id],
    enabled: puedeVerRecepciones,
    queryFn: () => pedir<Recepcion[]>(`/recepciones?proveedorId=${id}`),
  });
  const facturas = useQuery({
    queryKey: ['facturas-del-proveedor', id],
    enabled: puedeVerFacturas,
    queryFn: () => pedir<FacturaDeProveedor[]>(`/facturas-de-proveedor?proveedorId=${id}`),
  });

  const guardarPerfil = useMutation({
    mutationFn: () =>
      pedir<void>(`/terceros/${id}/proveedor`, {
        method: 'PUT',
        cuerpo: { diasDePago: Number((dias ?? '0').trim() === '' ? '0' : (dias ?? '0').trim()) },
      }),
    onSuccess: async () => {
      setError(null);
      setDias(null);
      await cola.invalidateQueries({ queryKey: ['tercero', id] });
      await cola.invalidateQueries({ queryKey: ['terceros'] });
    },
    onError: (fallo: unknown) => {
      setError(textoDeError(fallo));
    },
  });

  if (tercero.isPending) return <Cargando />;
  if (tercero.isError) {
    return (
      <Problema
        error={tercero.error}
        reintentar={() => {
          void tercero.refetch();
        }}
      />
    );
  }
  const ficha = tercero.data;
  if (ficha === undefined) return <Vacio />;

  return (
    <>
      <Encabezado
        titulo={ficha.razonSocial}
        descripcion={ficha.identificacion ?? ficha.nombreComercial ?? ''}
        acciones={
          <>
            <Insignia tono={ficha.activo ? 'bien' : 'neutro'}>
              {ficha.activo ? t('catalogo.activo') : t('catalogo.inactivo')}
            </Insignia>
            {ficha.esCliente ? (
              <Link to="/clientes/$id" params={{ id }}>
                <Boton tono="secundario">{t('ficha.tambienCliente')}</Boton>
              </Link>
            ) : null}
            <Link to="/proveedores">
              <Boton tono="secundario">{t('acciones.volver')}</Boton>
            </Link>
          </>
        }
      />

      <div className="flex flex-col gap-4">
        <DatosDelTercero
          tercero={ficha}
          puedeEditar={puedeEditar}
          alGuardar={['tercero', 'terceros']}
        />

        <div className="grid gap-4 lg:grid-cols-2">
          <Tarjeta
            titulo={t('ficha.perfilDeProveedor')}
            descripcion={t('ficha.ayudaPerfilDeProveedor')}
            acciones={
              puedeEditarPerfil ? (
                <Boton
                  cargando={guardarPerfil.isPending}
                  onClick={() => {
                    guardarPerfil.mutate();
                  }}
                >
                  {t('acciones.guardar')}
                </Boton>
              ) : undefined
            }
          >
            <div className="flex flex-col gap-2">
              <Campo
                etiqueta={t('clientes.diasDePago')}
                inputMode="numeric"
                disabled={!puedeEditarPerfil}
                value={dias ?? String(ficha.diasDePago ?? 0)}
                onChange={(evento) => {
                  setDias(evento.target.value);
                }}
              />
              {error === null ? null : (
                <p role="alert" className="text-xs text-mal">
                  {error}
                </p>
              )}
            </div>
          </Tarjeta>

          {puedeVerCuentas ? (
            <Tarjeta
              titulo={t('ficha.cuentaCorriente')}
              descripcion={t('ficha.ayudaCuentaProveedor')}
              acciones={
                <Link to="/estado-de-cuenta/$terceroId" params={{ terceroId: id }}>
                  <Boton tono="secundario">{t('cuentas.estadoDeCuenta')}</Boton>
                </Link>
              }
            >
              {cuenta.isPending ? <Cargando /> : null}
              {cuenta.isSuccess ? (
                <div className="flex flex-col gap-2">
                  <p className="numero text-titulo font-semibold">
                    {importeParaMirar(cuenta.data.saldo, i18n.language)}
                  </p>
                  <Tabla
                    columnas={[
                      {
                        clave: 'cuando',
                        titulo: t('existencias.cuando'),
                        contenido: (fila: MovimientoDeCuenta) =>
                          new Date(fila.ocurridoEn).toLocaleDateString(),
                      },
                      {
                        clave: 'concepto',
                        titulo: t('cuentas.concepto'),
                        contenido: (fila: MovimientoDeCuenta) => fila.concepto ?? fila.tipo,
                      },
                      {
                        clave: 'importe',
                        titulo: t('ficha.importe'),
                        numerica: true,
                        contenido: (fila: MovimientoDeCuenta) =>
                          importeParaMirar(fila.importe, i18n.language),
                      },
                    ]}
                    filas={cuenta.data.movimientos.slice(0, 10)}
                    claveDeFila={(fila) => fila.id}
                    vacio={<Vacio titulo={t('ficha.sinCuenta')} />}
                  />
                </div>
              ) : null}
            </Tarjeta>
          ) : null}
        </div>

        {puedeVerOrdenes ? (
          <Tarjeta titulo={t('compras.ordenes')} descripcion={t('ficha.ayudaOrdenes')}>
            {ordenes.isPending ? <Cargando /> : null}
            {ordenes.isSuccess ? (
              <Tabla
                columnas={[
                  {
                    clave: 'numero',
                    titulo: t('compras.numero'),
                    contenido: (fila: Orden) => fila.numero,
                  },
                  {
                    clave: 'fecha',
                    titulo: t('compras.fecha'),
                    contenido: (fila: Orden) => fila.fecha,
                  },
                  {
                    clave: 'estado',
                    titulo: t('catalogo.estado'),
                    contenido: (fila: Orden) => (
                      <Insignia tono="neutro">
                        {t(claveDeEstado(fila.estado), fila.estado)}
                      </Insignia>
                    ),
                  },
                  {
                    clave: 'total',
                    titulo: t('venta.total'),
                    numerica: true,
                    contenido: (fila: Orden) => importeParaMirar(fila.total, i18n.language),
                  },
                ]}
                filas={ordenes.data}
                claveDeFila={(fila) => fila.id}
                vacio={<Vacio titulo={t('ficha.sinOrdenes')} />}
              />
            ) : null}
          </Tarjeta>
        ) : null}

        <div className="grid gap-4 lg:grid-cols-2">
          {puedeVerRecepciones ? (
            <Tarjeta titulo={t('compras.recepciones')} descripcion={t('ficha.ayudaRecepciones')}>
              {recepciones.isPending ? <Cargando /> : null}
              {recepciones.isSuccess ? (
                <Tabla
                  columnas={[
                    {
                      clave: 'numero',
                      titulo: t('compras.numero'),
                      contenido: (fila: Recepcion) => fila.numero,
                    },
                    {
                      clave: 'fecha',
                      titulo: t('compras.fecha'),
                      contenido: (fila: Recepcion) => fila.fecha,
                    },
                    {
                      clave: 'remito',
                      titulo: t('compras.remito'),
                      contenido: (fila: Recepcion) => fila.remitoDelProveedor ?? '',
                    },
                  ]}
                  filas={recepciones.data}
                  claveDeFila={(fila) => fila.id}
                  vacio={<Vacio titulo={t('ficha.sinRecepciones')} />}
                />
              ) : null}
            </Tarjeta>
          ) : null}

          {puedeVerFacturas ? (
            <Tarjeta titulo={t('compras.facturas')} descripcion={t('ficha.ayudaFacturas')}>
              {facturas.isPending ? <Cargando /> : null}
              {facturas.isSuccess ? (
                <Tabla
                  columnas={[
                    {
                      clave: 'numero',
                      titulo: t('compras.numero'),
                      contenido: (fila: FacturaDeProveedor) => numeroDeLaFactura(fila),
                    },
                    {
                      clave: 'fecha',
                      titulo: t('compras.fecha'),
                      contenido: (fila: FacturaDeProveedor) => fila.fecha,
                    },
                    {
                      clave: 'total',
                      titulo: t('venta.total'),
                      numerica: true,
                      contenido: (fila: FacturaDeProveedor) =>
                        importeParaMirar(fila.total, i18n.language),
                    },
                    {
                      clave: 'cotejo',
                      titulo: '',
                      // La diferencia se marca acá: es lo que hay que mirar antes de pagar.
                      contenido: (fila: FacturaDeProveedor) =>
                        fila.tieneDiferencias ? (
                          <Insignia tono="mal">{t('compras.conDiferencias')}</Insignia>
                        ) : null,
                    },
                  ]}
                  filas={facturas.data}
                  claveDeFila={(fila) => fila.id}
                  vacio={<Vacio titulo={t('ficha.sinFacturas')} />}
                />
              ) : null}
            </Tarjeta>
          ) : null}
        </div>
      </div>
    </>
  );
}
